"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "../hooks/useTranslation";

export function HowToPlay() {
  const { t } = useTranslation();
  const [activeSteps, setActiveSteps] = useState<number[]>([]);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    stepRefs.current.forEach((ref, index) => {
      if (!ref) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const stepNumber = index + 1;
            if (entry.isIntersecting) {
              setActiveSteps((prev) => {
                if (!prev.includes(stepNumber)) {
                  return [...prev, stepNumber].sort((a, b) => a - b);
                }
                return prev;
              });
            } else {
              // Remove from active when scrolling up/out of view
              setActiveSteps((prev) => {
                return prev.filter((num) => num !== stepNumber);
              });
            }
          });
        },
        {
          threshold: 0.5,
          rootMargin: "-100px 0px",
        }
      );

      observer.observe(ref);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  const steps = [
    {
      number: 1,
      title: t("home.howToPlay.step1Title"),
      description: t("home.howToPlay.step1Description"),
      image: "/assets/usp-step-1.png",
    },
    {
      number: 2,
      title: t("home.howToPlay.step2Title"),
      description: t("home.howToPlay.step2Description"),
      image: "/assets/usp-step-2.png",
    },
    {
      number: 3,
      title: t("home.howToPlay.step3Title"),
      description: t("home.howToPlay.step3Description"),
      image: "/assets/usp-step-3.png",
    },
  ];

  // Calculate line position - from middle of first step to middle of last step
  const [lineStart, setLineStart] = useState(0);
  const [lineEnd, setLineEnd] = useState(0);
  const [lineEndFull, setLineEndFull] = useState(0); // For gray background line (to last step)
  const [scrollProgress, setScrollProgress] = useState(0); // For gradual red line fill
  const [redLineEndPosition, setRedLineEndPosition] = useState(0); // Absolute position of red line end
  const bulletRefs = useRef<(HTMLDivElement | null)[]>([]);
  const sectionRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const calculateLinePosition = () => {
      const container = containerRef.current;
      if (!container) return;

      // Get first step position (step 1) - use the middle of the step
      const firstStep = stepRefs.current[0];
      const lastStep = stepRefs.current[steps.length - 1];
      
      if (!firstStep || !lastStep) return;

      const containerRect = container.getBoundingClientRect();
      const firstStepRect = firstStep.getBoundingClientRect();
      const lastStepRect = lastStep.getBoundingClientRect();
      
      const firstStepMiddle = firstStepRect.top - containerRect.top + firstStepRect.height / 2;
      const lastStepMiddle = lastStepRect.top - containerRect.top + lastStepRect.height / 2;

      const containerHeight = containerRect.height;
      
      setLineStart((firstStepMiddle / containerHeight) * 100);
      setLineEndFull((lastStepMiddle / containerHeight) * 100);

      // Calculate scroll progress for gradual red line fill
      const section = sectionRef.current;
      if (section) {
        const sectionRect = section.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const sectionTop = sectionRect.top;
        const sectionHeight = sectionRect.height;
        
        // Calculate progress: 0 when section top enters viewport, 1 when section bottom leaves viewport
        // Use viewport center as reference point for smoother animation
        const viewportCenter = windowHeight / 2;
        const sectionStart = sectionTop;
        const sectionEnd = sectionTop + sectionHeight;
        
        // Progress from 0 to 1 as viewport center moves through the section
        let progress = 0;
        if (viewportCenter >= sectionStart && viewportCenter <= sectionEnd) {
          progress = (viewportCenter - sectionStart) / (sectionEnd - sectionStart);
        } else if (viewportCenter > sectionEnd) {
          progress = 1;
        }
        
        progress = Math.max(0, Math.min(1, progress));
        setScrollProgress(progress);
        
        // Calculate red line end based on scroll progress
        const redLineEnd = firstStepMiddle + (lastStepMiddle - firstStepMiddle) * progress;
        setLineEnd((redLineEnd / containerHeight) * 100);
        setRedLineEndPosition(redLineEnd); // Store absolute position for dot color calculation
      } else {
        // Fallback: use active steps
        if (activeSteps.length > 0) {
          const maxActiveStep = Math.max(...activeSteps);
          const activeStep = stepRefs.current[maxActiveStep - 1];
          if (activeStep) {
            const activeStepRect = activeStep.getBoundingClientRect();
            const activeStepMiddle = activeStepRect.top - containerRect.top + activeStepRect.height / 2;
            setLineEnd((activeStepMiddle / containerHeight) * 100);
          } else {
            setLineEnd((firstStepMiddle / containerHeight) * 100);
          }
        } else {
          setLineEnd((firstStepMiddle / containerHeight) * 100);
        }
      }
    };

    // Use requestAnimationFrame to ensure DOM is updated
    requestAnimationFrame(calculateLinePosition);
    window.addEventListener("resize", calculateLinePosition);
    window.addEventListener("scroll", calculateLinePosition);
    
    return () => {
      window.removeEventListener("resize", calculateLinePosition);
      window.removeEventListener("scroll", calculateLinePosition);
    };
  }, [activeSteps, steps]);

  return (
    <section ref={sectionRef} className="w-full bg-[var(--rtbf-blue-dark,#003E63)] pt-8 pb-12 md:pb-16">
      <div className="container mx-auto max-w-7xl px-3 sm:px-4 lg:px-0">
        <h2 className="text-xl md:text-2xl lg:text-4xl font-bold text-white text-center mb-8 md:mb-12">
          {t("home.howToPlay.title")}
        </h2>

        <div className="relative">
          {/* Steps Container */}
          <div ref={containerRef} className="space-y-12 md:space-y-16 relative">
            {/* Steps */}
            {steps.map((step, index) => {
              // Calculate if dot should be red based on current red line position
              // Dot is red only if the red line has reached or passed its middle point
              let isRed = false;
              const stepRef = stepRefs.current[index];
              const container = containerRef.current;
              if (stepRef && container && redLineEndPosition > 0) {
                const containerRect = container.getBoundingClientRect();
                const stepRect = stepRef.getBoundingClientRect();
                const stepMiddle = stepRect.top - containerRect.top + stepRect.height / 2;
                isRed = redLineEndPosition >= stepMiddle;
              }
              
              return (
                <div
                  key={step.number}
                  ref={(el) => {
                    stepRefs.current[index] = el;
                  }}
                  className="flex justify-center min-h-[200px]"
                >
                  <div className="w-full max-w-3xl flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8 rounded-2xl bg-[var(--rtbf-blue,#0076A9)] p-4 md:p-6">
                    <div className="w-full md:w-[200px] lg:w-[240px] aspect-square rounded-lg overflow-hidden shrink-0">
                      <img 
                        src={step.image} 
                        alt={`Step ${step.number}: ${step.title}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex flex-col justify-center w-full max-w-xl px-2 md:px-4 text-left">
                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                        {step.title}
                      </h3>
                      <p className="text-base md:text-lg lg:text-xl text-white/80 leading-relaxed font-normal">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

